import { useState } from "react";
import { Page } from "@boundary/layouts/pageLayout";
import { OnboardingElement } from "@boundary/widgets/onboarding";
import { LoginFlowData, LoginFlowSteps } from "@boundary/pages/3.login/loginFlow";
import { Button } from "@boundary/components/button";
import { LAW_DOCUMENTS } from "@shared/configs/externalUrls";
import { Check, ExternalLink } from "lucide-react";
import styles from "./legal.module.scss";

interface LegalCheckboxProps {
  label: string;
  href: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function LegalCheckbox({ label, href, checked, onChange }: LegalCheckboxProps) {
  return (
    <div className={styles.checkboxCard} onClick={() => onChange(!checked)}>
      <span className={`${styles.checkbox} ${checked ? styles.checked : ""}`}>
        {checked && <Check size={12} strokeWidth={3} />}
      </span>
      <span className={styles.label}>{label}</span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.linkIcon}
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink size={16} />
      </a>
    </div>
  );
}

interface LegalState {
  userAgreement: boolean;
  privacyPolicy: boolean;
  userConsent: boolean;
  offer: boolean;
  bonusRules: boolean;
  referralRules: boolean;
}

export const LegalStep: OnboardingElement<LoginFlowData, LoginFlowSteps> = (props) => {
  const [state, setState] = useState<LegalState>({
    userAgreement: false,
    privacyPolicy: false,
    userConsent: false,
    offer: false,
    bonusRules: false,
    referralRules: false
  });

  // Отмечены все до одного: список короткий, и «принять всё» одной кнопкой здесь
  // не даёт человеку даже увидеть, с чем он соглашается.
  const allChecked = Object.values(state).every(Boolean);

  const handleContinue = async () => {
    if (allChecked) {
      await props.onComplete("phone", {});
    }
  };

  return (
    <Page screenBackground="domain">
      <Page.SafeArea />
      <Page.DisplayZone>
        <div className={styles.container}>
          <Page.DisplayZone.Body className={styles.body}>
            <h1>Перед началом работы ознакомьтесь с документами</h1>
            <div className={styles.checkboxList}>
              <LegalCheckbox
                label="Пользовательское соглашение"
                href={LAW_DOCUMENTS.USER_AGREEMENT}
                checked={state.userAgreement}
                onChange={(checked) => setState((s) => ({ ...s, userAgreement: checked }))}
              />
              <LegalCheckbox
                label="Политика конфиденциальности"
                href={LAW_DOCUMENTS.PRIVACY_POLICY}
                checked={state.privacyPolicy}
                onChange={(checked) => setState((s) => ({ ...s, privacyPolicy: checked }))}
              />
              <LegalCheckbox
                label="Обработка персональных данных"
                href={LAW_DOCUMENTS.USER_CONSENT}
                checked={state.userConsent}
                onChange={(checked) => setState((s) => ({ ...s, userConsent: checked }))}
              />
              <LegalCheckbox
                label="Оферта"
                href={LAW_DOCUMENTS.OFFER}
                checked={state.offer}
                onChange={(checked) => setState((s) => ({ ...s, offer: checked }))}
              />
              {/*
                Бонусная и реферальная программы принимаются здесь же: участником
                обеих человек становится сразу после регистрации, и награда может
                прийти в тот же день — подарком за регистрацию или скидкой по
                приглашению. Спрашивать согласие в момент награды поздно.
              */}
              <LegalCheckbox
                label="Правила бонусной программы"
                href={LAW_DOCUMENTS.BONUS_RULES}
                checked={state.bonusRules}
                onChange={(checked) => setState((s) => ({ ...s, bonusRules: checked }))}
              />
              <LegalCheckbox
                label="Правила реферальной программы"
                href={LAW_DOCUMENTS.REFERRAL_RULES}
                checked={state.referralRules}
                onChange={(checked) => setState((s) => ({ ...s, referralRules: checked }))}
              />
            </div>
          </Page.DisplayZone.Body>
          <Page.DisplayZone.Sticky className={styles.buttonPad}>
            <Button disabled={!allChecked || props.disabled} onClick={handleContinue}>
              Продолжить
            </Button>
          </Page.DisplayZone.Sticky>
        </div>
      </Page.DisplayZone>
    </Page>
  );
};

